import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TESTIDS } from "@/constants/testIds";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const CURRENCIES = ["USD", "EUR", "GBP", "CHF", "CNY", "AED", "SGD"];
const LANGS = [{ v: "en", l: "English" }, { v: "it", l: "Italiano" }, { v: "de", l: "Deutsch" }];
const AUTHORITY_KEYS = ["Sole", "Joint", "Advisory"];
const AUTHORITY_I18N = { Sole: "cp_auth_sole", Joint: "cp_auth_joint", Advisory: "cp_auth_advisory" };

const EMPTY = {
  legal_name: "", registration_number: "", vat_number: "",
  registered_address: "", operational_address: "", country: "",
  website: "", phone: "", email: "",
  default_currency: "USD", default_language: "en",
  signatories: [],
  bank_details: "", footer_text: "",
};

export default function CompanyProfilePage() {
  const { t } = useI18n();
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const c = await api.getCompany();
        if (c && c.id) {
          setData({ ...EMPTY, ...c, signatories: c.signatories || [] });
        }
      } catch (e) { /* keep empty */ }
      setLoading(false);
    })();
  }, []);

  const update = (k, v) => setData((s) => ({ ...s, [k]: v }));
  const addSignatory = () => setData((s) => ({
    ...s, signatories: [...s.signatories, { name: "", title: "", authority: "Sole", email: "" }],
  }));
  const updateSignatory = (i, field, v) => setData((s) => ({
    ...s, signatories: s.signatories.map((sg, idx) => idx === i ? { ...sg, [field]: v } : sg),
  }));
  const removeSignatory = (i) => setData((s) => ({
    ...s, signatories: s.signatories.filter((_, idx) => idx !== i),
  }));

  const save = async () => {
    setSaving(true);
    try {
      const saved = await api.saveCompany(data);
      setData({ ...EMPTY, ...saved, signatories: saved.signatories || [] });
      toast.success(t("cp_saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("cp_save_failed"));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-12 text-[var(--td-text-3)] font-mono text-xs">{t("cp_loading")}</div>;

  return (
    <div className="px-4 py-6 sm:p-6 md:p-10 xl:p-12 max-w-5xl">
      <div className="mb-10">
        <div className="kicker mb-3">— {t("d_nav_company")}</div>
        <h1 className="font-serif text-4xl md:text-5xl mb-3">{t("d_nav_company")}</h1>
        <p className="text-[var(--td-text-2)] max-w-2xl leading-relaxed">{t("cp_intro")}</p>
      </div>

      <Section title={t("cp_sec_legal")}>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label={t("cp_f_legal_name")} testid={TESTIDS.cpLegalName} value={data.legal_name} onChange={(v) => update("legal_name", v)} />
          <Field label={t("cp_f_country")} testid={TESTIDS.cpCountry} value={data.country || ""} onChange={(v) => update("country", v)} />
          <Field label={t("cp_f_registration")} testid={TESTIDS.cpRegistration} value={data.registration_number || ""} onChange={(v) => update("registration_number", v)} />
          <Field label={t("cp_f_vat")} testid={TESTIDS.cpVat} value={data.vat_number || ""} onChange={(v) => update("vat_number", v)} />
          <Field label={t("cp_f_registered_addr")} testid={TESTIDS.cpRegisteredAddress} textarea value={data.registered_address || ""} onChange={(v) => update("registered_address", v)} />
          <Field label={t("cp_f_operational_addr")} testid={TESTIDS.cpOperationalAddress} textarea value={data.operational_address || ""} onChange={(v) => update("operational_address", v)} />
        </div>
      </Section>

      <Section title={t("cp_sec_contact")}>
        <div className="grid md:grid-cols-3 gap-5">
          <Field label={t("cp_f_website")} testid={TESTIDS.cpWebsite} value={data.website || ""} onChange={(v) => update("website", v)} />
          <Field label={t("cp_f_phone")} testid={TESTIDS.cpPhone} value={data.phone || ""} onChange={(v) => update("phone", v)} />
          <Field label={t("cp_f_email")} testid={TESTIDS.cpEmail} value={data.email || ""} onChange={(v) => update("email", v)} />
        </div>
      </Section>

      <Section title={t("cp_sec_defaults")}>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className="td-label">{t("cp_f_currency")}</label>
            <select data-testid={TESTIDS.cpDefaultCurrency} className="td-input"
                    value={data.default_currency || "USD"}
                    onChange={(e) => update("default_currency", e.target.value)}>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="td-label">{t("cp_f_lang")}</label>
            <select data-testid={TESTIDS.cpDefaultLanguage} className="td-input"
                    value={data.default_language || "en"}
                    onChange={(e) => update("default_language", e.target.value)}>
              {LANGS.map((l) => <option key={l.v} value={l.v}>{l.l}</option>)}
            </select>
          </div>
        </div>
      </Section>

      <Section title={t("cp_sec_signatories")}>
        <div className="space-y-3 mb-3">
          {data.signatories.map((s, i) => (
            <div key={i} className="grid grid-cols-[1.4fr_1.2fr_1fr_auto] gap-2 items-end">
              <div>
                <label className="td-label">{t("cp_f_name")}</label>
                <input data-testid={`${TESTIDS.cpSignName}-${i}`} className="td-input !py-1.5 !text-sm"
                       value={s.name} onChange={(e) => updateSignatory(i, "name", e.target.value)} />
              </div>
              <div>
                <label className="td-label">{t("cp_f_title")}</label>
                <input data-testid={`${TESTIDS.cpSignTitle}-${i}`} className="td-input !py-1.5 !text-sm"
                       value={s.title || ""} onChange={(e) => updateSignatory(i, "title", e.target.value)} />
              </div>
              <div>
                <label className="td-label">{t("cp_f_authority")}</label>
                <select data-testid={`${TESTIDS.cpSignAuthority}-${i}`} className="td-input !py-1.5 !text-sm"
                        value={s.authority || "Sole"} onChange={(e) => updateSignatory(i, "authority", e.target.value)}>
                  {AUTHORITY_KEYS.map((a) => <option key={a} value={a}>{t(AUTHORITY_I18N[a])}</option>)}
                </select>
              </div>
              <button type="button" onClick={() => removeSignatory(i)}
                      data-testid={`${TESTIDS.cpSignRemove}-${i}`}
                      className="text-[var(--td-text-3)] hover:text-red-400 pb-2.5">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
        <button type="button" onClick={addSignatory}
                data-testid={TESTIDS.cpSignAdd}
                className="font-mono text-[11px] uppercase tracking-widest text-[var(--td-gold)] hover:text-[var(--td-gold-hover)] inline-flex items-center gap-1.5">
          <Plus size={12} /> {t("cp_add_sign")}
        </button>
      </Section>

      <Section title={t("cp_sec_bank")}>
        <Field label={t("cp_f_bank")} testid={TESTIDS.cpBankDetails} textarea rows={3} value={data.bank_details || ""} onChange={(v) => update("bank_details", v)}
               help={t("cp_f_bank_help")} />
        <div className="mt-5" />
        <Field label={t("cp_f_footer")} testid={TESTIDS.cpFooter} textarea rows={3} value={data.footer_text || ""} onChange={(v) => update("footer_text", v)}
               help={t("cp_f_footer_help")} />
      </Section>

      <div className="mt-10 flex justify-end">
        <button data-testid={TESTIDS.cpSaveBtn} disabled={saving}
                onClick={save}
                className="btn-gold inline-flex items-center gap-2 disabled:opacity-50">
          <Save size={14} /> {saving ? t("cp_saving") : t("cp_save_btn")}
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-10">
      <div className="font-mono text-[11px] uppercase tracking-widest text-[var(--td-gold)] mb-4">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, testid, value, onChange, textarea = false, rows = 2, help }) {
  return (
    <div>
      <label className="td-label">{label}</label>
      {textarea ? (
        <textarea data-testid={testid} className="td-input resize-none" rows={rows}
                  value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input data-testid={testid} className="td-input"
               value={value} onChange={(e) => onChange(e.target.value)} />
      )}
      {help && <div className="mt-1 text-xs text-[var(--td-text-3)]">{help}</div>}
    </div>
  );
}
